import { PageTitle, Btn, Card, Spinner } from "../components";
import { useEffect, useMemo, useState } from "react";
import { apiFetch } from "../api";
import { formatEmbraerHorarios } from "../utils/embraerColumns";
import { formatTrechoCurto, getDestinoLabel, getIntegracaoNome } from "../utils/tenantLabels";
import { useTenant } from "../context/TenantContext";
import { recordUiError } from "../observability";
import { parseEmbraerWorkbookFromFile } from "../utils/embraerParse";

const formatDateBr = (value) => {
  if (!value) return "—";
  const [year, month, day] = String(value).substring(0, 10).split("-");
  if (!year || !month || !day) return value;
  return `${day}/${month}/${year}`;
};

const SEVERIDADE_STYLE = {
  erro: { bg: "rgba(239,68,68,0.12)", color: "#fca5a5", label: "Erro" },
  aviso: { bg: "rgba(234,179,8,0.15)", color: "#fde047", label: "Aviso" },
  ok: { bg: "rgba(34,197,94,0.15)", color: "#86efac", label: "OK" },
};

const worstSeveridade = (current, next) => {
  if (current === "erro" || next === "erro") return "erro";
  if (current === "aviso" || next === "aviso") return "aviso";
  return "ok";
};

const buildContextoProgramacao = (programacao, config) => {
  if (!programacao) return "";
  return `${formatDateBr(programacao.data)} · ${formatEmbraerHorarios(programacao)} · ${formatTrechoCurto(programacao.trecho, config)}`;
};

const groupAchadosIntoItens = (achados = [], programacaoByKey = new Map(), config = {}) => {
  const map = new Map();

  for (const achado of achados) {
    const key = achado.programacao_key || achado.key || achado.id;
    if (!key) continue;

    if (!map.has(key)) {
      const programacao = programacaoByKey.get(key);
      map.set(key, {
        key,
        id_pedido: achado.id_pedido || programacao?.id_pedido || "—",
        contexto: buildContextoProgramacao(programacao, config) || achado.detalhe || "",
        severidade: achado.severidade || "aviso",
        rota_id: achado.rota_id || programacao?.rota_id || null,
        programacao_id: achado.programacao_id || programacao?.programacao_id || null,
        pode_criar: !!programacao?.pode_criar,
        pendencias: [],
      });
    }

    const item = map.get(key);
    item.severidade = worstSeveridade(item.severidade, achado.severidade);
    if (!item.rota_id && achado.rota_id) item.rota_id = achado.rota_id;

    const detalhePendencia = achado.detalhe && achado.detalhe !== item.contexto
      ? achado.detalhe
      : null;

    item.pendencias.push({
      tipo: achado.tipo,
      severidade: achado.severidade || "aviso",
      texto: achado.texto || achado.mensagem || "—",
      detalhe: detalhePendencia,
    });
  }

  return [...map.values()];
};

const normalizeGrupoItens = (grupo, programacaoByKey, config = {}) => {
  if (Array.isArray(grupo.itens) && grupo.itens.length) {
    return grupo.itens.map((item) => ({
      ...item,
      pendencias: item.pendencias || [],
    }));
  }

  if (Array.isArray(grupo.achados) && grupo.achados.length) {
    return groupAchadosIntoItens(grupo.achados, programacaoByKey, config);
  }

  return [];
};

const buildGruposFromProgramacoes = (programacoes = []) => [
  {
    id: "nao_cadastrada",
    titulo: "Programações não cadastradas",
    itens: programacoes.filter((item) => item.situacao === "nova"),
  },
  {
    id: "divergencias",
    titulo: "Divergências nas programações",
    itens: programacoes.filter((item) => item.situacao === "existente" && item.severidade !== "ok"),
  },
  {
    id: "ok",
    titulo: "Sem pendências",
    itens: programacoes.filter((item) => item.severidade === "ok"),
  },
].filter((grupo) => grupo.itens.length > 0);

const normalizeAuditoria = (auditoria, programacoes = [], config = {}) => {
  if (!auditoria) return null;

  const programacaoByKey = new Map(
    programacoes.map((item) => [item.key, item])
  );

  const cards = (auditoria.programacoes || [])
    .filter((item) => Array.isArray(item.pendencias))
    .map((item) => ({
      ...item,
      pendencias: item.pendencias || [],
    }));

  let grupos = (auditoria.grupos || []).map((grupo) => ({
    ...grupo,
    itens: normalizeGrupoItens(grupo, programacaoByKey, config),
  })).filter((grupo) => grupo.itens.length > 0);

  if (!grupos.length && cards.length) {
    grupos = buildGruposFromProgramacoes(cards);
  }

  const itens = grupos.flatMap((grupo) => grupo.itens);

  return {
    ...auditoria,
    programacoes: cards.length ? cards : itens,
    grupos,
    resumo: {
      programacoes: auditoria.resumo?.programacoes ?? itens.length,
      com_pendencia: auditoria.resumo?.com_pendencia ?? itens.filter(
        (item) => item.severidade === "erro" || item.severidade === "aviso"
      ).length,
      nao_cadastradas: auditoria.resumo?.nao_cadastradas ?? 0,
      com_divergencia: auditoria.resumo?.com_divergencia ?? 0,
      prontas_para_criar: auditoria.resumo?.prontas_para_criar ?? 0,
      conferidas: auditoria.resumo?.conferidas ?? 0,
      tem_pendencias: auditoria.resumo?.tem_pendencias ?? itens.some(
        (item) => item.severidade === "erro" || item.severidade === "aviso"
      ),
      ...auditoria.resumo,
    },
  };
};

const SeveridadeBadge = ({ severidade }) => {
  const style = SEVERIDADE_STYLE[severidade] || SEVERIDADE_STYLE.aviso;
  return (
    <span style={{
      fontSize: 10,
      fontWeight: 700,
      padding: "3px 7px",
      borderRadius: 999,
      background: style.bg,
      color: style.color,
      textTransform: "uppercase",
      letterSpacing: "0.03em",
    }}>
      {style.label}
    </span>
  );
};

export default function BuscarRotasEmbraerPage({ onOpenPedidos, onOpenProgramacoes, userRole }) {
  const openPedidos = onOpenPedidos || onOpenProgramacoes;
  const isSuperadmin = userRole === "superadmin";
  const { config } = useTenant();
  const [file, setFile] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [fetchingAuto, setFetchingAuto] = useState(false);
  const [fonteDados, setFonteDados] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [parseInfo, setParseInfo] = useState(null);
  const [parsedProgramacoes, setParsedProgramacoes] = useState([]);
  const [preview, setPreview] = useState(null);
  const [step, setStep] = useState("upload");
  const [selectedKeys, setSelectedKeys] = useState(new Set());
  const [toast, setToast] = useState("");
  const [toastType, setToastType] = useState("success");
  const [clientes, setClientes] = useState([]);
  const [clientesLoading, setClientesLoading] = useState(isSuperadmin);
  const [clienteId, setClienteId] = useState(() => (
    isSuperadmin ? localStorage.getItem("rotaflex_embraer_cliente_id") || "" : ""
  ));

  const showMessage = (message, type = "success") => {
    if (type === "error") {
      recordUiError({ message, page: "buscar_embraer", source: "toast" });
    }
    setToastType(type);
    setToast(message);
    setTimeout(() => setToast(""), 5000);
  };

  useEffect(() => {
    if (!isSuperadmin) return undefined;
    let active = true;
    setClientesLoading(true);
    apiFetch("/embraer/clientes")
      .then((rows) => {
        if (!active) return;
        const list = (Array.isArray(rows) ? rows : []).filter((cliente) => cliente.ativo !== false);
        setClientes(list);
        setClienteId((current) => {
          const valid = list.some((cliente) => String(cliente.id) === String(current));
          const next = valid ? String(current) : "";
          if (!next) localStorage.removeItem("rotaflex_embraer_cliente_id");
          return next;
        });
      })
      .catch((error) => {
        if (active) showMessage(error?.message || "Erro ao carregar clientes.", "error");
      })
      .finally(() => {
        if (active) setClientesLoading(false);
      });
    return () => { active = false; };
  }, [isSuperadmin]);

  const selectedClienteBody = () => (
    isSuperadmin ? { cliente_id: Number(clienteId) || null } : {}
  );

  const validateSelectedCliente = () => {
    if (isSuperadmin && !clienteId) {
      showMessage("Selecione o cliente que receberá os pedidos antes de continuar.", "error");
      return false;
    }
    return true;
  };

  const prontasParaCriar = useMemo(
    () => (preview?.programacoes || []).filter((item) => item.pode_criar),
    [preview]
  );

  const resetPreviewState = () => {
    setPreview(null);
    setStep("upload");
    setSelectedKeys(new Set());
    setFonteDados(null);
  };

  const handleFileChange = async (event) => {
    const nextFile = event.target.files?.[0] || null;
    setFile(nextFile);
    resetPreviewState();
    setParsedProgramacoes([]);
    setParseInfo(null);

    if (!nextFile) return;

    setFonteDados("planilha");
    setParsing(true);
    try {
      const parsed = await parseEmbraerWorkbookFromFile(nextFile);
      setParsedProgramacoes(parsed.programacoes);
      setParseInfo({
        total_linhas: parsed.total_linhas,
        total_programacoes: parsed.programacoes.length,
        programacoes_ignoradas: parsed.programacoes_ignoradas || 0,
        total_linhas_ignoradas: parsed.total_linhas_ignoradas || 0,
      });
      const ignoradas = parsed.programacoes_ignoradas || 0;
      showMessage(
        ignoradas > 0
          ? `${parsed.programacoes.length} pedido(s) para auditar · ${ignoradas} ignorado(s) (Recusado pela central).`
          : `${parsed.programacoes.length} pedido(s) identificados na planilha.`
      );
    } catch (error) {
      showMessage(error.message || "Falha ao ler a planilha.", "error");
    } finally {
      setParsing(false);
    }
  };

  const handleFetchAuto = async () => {
    if (!validateSelectedCliente()) return;
    setFetchingAuto(true);
    resetPreviewState();
    setParsedProgramacoes([]);
    setParseInfo(null);
    setFile(null);

    const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

    try {
      const start = await apiFetch("/embraer/fetch-auto", {
        method: "POST",
        body: JSON.stringify({ days: 7, ...selectedClienteBody() }),
      });

      if (!start?.job_id) {
        throw new Error("Resposta inválida ao iniciar busca automática.");
      }

      let result = null;
      for (let attempt = 0; attempt < 120; attempt += 1) {
        await sleep(attempt === 0 ? 1500 : 3000);
        const job = await apiFetch(`/embraer/fetch-auto/jobs/${start.job_id}`);

        if (job.status === "processing") continue;

        if (job.status === "error") {
          const err = new Error(job.erro || "Falha ao buscar rotas automaticamente.");
          err.code = job.code || null;
          err.status = job.code === "LOGIN_TIMEOUT" ? 408 : 500;
          throw err;
        }

        result = job;
        break;
      }

      if (!result || result.status !== "done") {
        throw new Error("A busca automática demorou demais. Tente novamente em instantes.");
      }

      const programacoes = result.programacoes || [];
      setParsedProgramacoes(programacoes);
      setFonteDados("automatico");
      setParseInfo({
        total_linhas: result.total_linhas || 0,
        total_programacoes: result.total_programacoes || programacoes.length,
        programacoes_ignoradas: result.programacoes_ignoradas || 0,
        total_linhas_ignoradas: result.total_linhas_ignoradas || 0,
        start_date: result.start_date,
        end_date: result.end_date,
      });

      if (!programacoes.length) {
        showMessage(
          result.aviso || "Nenhum pedido encontrado nos próximos 7 dias.",
          "error"
        );
        return;
      }

      const ignoradas = result.programacoes_ignoradas || 0;
      showMessage(
        ignoradas > 0
          ? `${programacoes.length} pedido(s) importados do SGT · ${ignoradas} ignorado(s).`
          : `${programacoes.length} pedido(s) importados do portal Embraer.`
      );
    } catch (error) {
      if (error.code === "LOGIN_TIMEOUT" || error.status === 408) {
        showMessage(
          "Aguardando login no portal Embraer. Confira o WhatsApp e o número no Authenticator; depois tente novamente.",
          "error"
        );
      } else if (error.code === "SERVICO_INDISPONIVEL" || error.status === 502 || error.status === 504) {
        showMessage("Serviço de automação Embraer indisponível no momento.", "error");
      } else {
        showMessage(error.message || "Falha ao buscar rotas automaticamente.", "error");
      }
    } finally {
      setFetchingAuto(false);
    }
  };

  const handlePreview = async () => {
    if (!validateSelectedCliente()) return;
    if (!parsedProgramacoes.length) {
      showMessage("Importe uma planilha válida antes de auditar.", "error");
      return;
    }

    setPreviewing(true);
    try {
      const result = await apiFetch("/embraer/preview", {
        method: "POST",
        body: JSON.stringify({
          programacoes: parsedProgramacoes,
          ...selectedClienteBody(),
        }),
      });
      setPreview(result);
      setSelectedKeys(new Set(
        (result.programacoes || [])
          .filter((item) => item.pode_criar)
          .map((item) => item.key)
      ));
      setStep("auditoria");
      showMessage("Auditoria concluída. Revise os achados antes de prosseguir.");
    } catch (error) {
      showMessage(error.message || "Erro ao auditar programações.", "error");
    } finally {
      setPreviewing(false);
    }
  };

  const toggleSelection = (key) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleConfirm = async () => {
    if (!validateSelectedCliente()) return;
    if (!selectedKeys.size) {
      showMessage("Selecione ao menos um pedido para criar.", "error");
      return;
    }

    setConfirming(true);
    try {
      const result = await apiFetch("/embraer/confirm", {
        method: "POST",
        body: JSON.stringify({
          programacoes: parsedProgramacoes,
          keys: [...selectedKeys],
          ...selectedClienteBody(),
        }),
      });
      const created = Array.isArray(result?.programacoes) ? result.programacoes : [];
      const totalIgnoradas = Number(result?.total_ignoradas || 0);
      let message = `${result.total_criadas} pedido(s) criado(s).`;
      if (totalIgnoradas > 0) {
        message += ` ${totalIgnoradas} já existente(s) e ignorado(s) na reimportação.`;
      }
      showMessage(message);
      if (openPedidos && created.length) {
        const focusDate = created[0]?.data ? String(created[0].data).substring(0, 10) : null;
        openPedidos({
          tab: "a_fazer",
          highlightIds: created.map((item) => Number(item.id)),
          focusDate,
        });
        return;
      }
      await handlePreview();
      setStep("auditoria");
    } catch (error) {
      showMessage(error.message || "Erro ao criar pedidos.", "error");
    } finally {
      setConfirming(false);
    }
  };

  const destinoLabel = getDestinoLabel(config);
  const auditoria = useMemo(
    () => normalizeAuditoria(preview?.auditoria, preview?.programacoes, config),
    [preview, config]
  );
  const resumoAuditoria = auditoria?.resumo;

  return (
    <div className="fade-in">
      <div style={{ marginBottom: 20 }}>
        <PageTitle icon="import">Importar pedidos {getIntegracaoNome(config) || "Embraer"}</PageTitle>
        <p style={{ color: "var(--muted)", fontSize: 14, marginTop: 4, lineHeight: 1.5 }}>
          Importe a planilha exportada do portal corporativo ou busque automaticamente no relatório SGT
          (hoje até +7 dias). Confira o resultado e escolha os pedidos que deseja cadastrar antes de montar as vans.
        </p>
      </div>

      {isSuperadmin && (
        <Card style={{ marginBottom: 20, borderColor: clienteId ? "rgba(34,197,94,0.35)" : "rgba(239,68,68,0.45)" }}>
          <label htmlFor="embraer-cliente" style={{ display: "block", fontSize: 12, color: "var(--muted)", fontWeight: 700, marginBottom: 7 }}>
            Cliente que receberá os pedidos
          </label>
          <select
            id="embraer-cliente"
            value={clienteId}
            disabled={clientesLoading || confirming || previewing || fetchingAuto || parsing}
            onChange={(event) => {
              const next = event.target.value;
              setClienteId(next);
              resetPreviewState();
              setParsedProgramacoes([]);
              setParseInfo(null);
              setFile(null);
              if (next) localStorage.setItem("rotaflex_embraer_cliente_id", next);
              else localStorage.removeItem("rotaflex_embraer_cliente_id");
            }}
            style={{ width: "100%", maxWidth: 420, padding: "9px 11px", borderRadius: 8, border: "1px solid var(--border)", background: "var(--s1)", color: "var(--text)", fontSize: 14 }}
          >
            <option value="">{clientesLoading ? "Carregando clientes..." : "Selecione o cliente"}</option>
            {clientes.map((cliente) => (
              <option key={cliente.id} value={cliente.id}>{cliente.nome}</option>
            ))}
          </select>
          <div style={{ marginTop: 8, fontSize: 12, color: clienteId ? "#86efac" : "#fca5a5" }}>
            {clienteId
              ? "A auditoria e a criação serão isoladas neste cliente."
              : "Obrigatório para impedir que o superadmin misture cadastros de clientes diferentes."}
          </div>
        </Card>
      )}

      {step !== "upload" && (
        <div style={{
          display: "flex",
          gap: 8,
          marginBottom: 16,
          flexWrap: "wrap",
          fontSize: 12,
        }}>
          {[
            { id: "upload", label: "1. Planilha", active: step === "upload" },
            { id: "auditoria", label: "2. Conferir pedidos", active: step === "auditoria" },
            { id: "acoes", label: "3. Cadastrar pedidos", active: step === "acoes" },
          ].map((item, index) => (
            <span
              key={item.id}
              style={{
                padding: "6px 12px",
                borderRadius: 999,
                fontWeight: 600,
                background: item.active ? "rgba(59,130,246,0.2)" : "var(--s2)",
                color: item.active ? "#93c5fd" : "var(--muted)",
              }}
            >
              {item.label}
            </span>
          ))}
        </div>
      )}

      <Card style={{ marginBottom: 20 }}>
        <div style={{ display: "grid", gap: 14 }}>
          <div>
            <label htmlFor="embraer-planilha" style={{ display: "block", fontSize: '0.875rem', color: "var(--muted)", fontWeight: 600, marginBottom: 8 }}>
              Planilha do portal (.xlsx)
            </label>
            <input
              id="embraer-planilha"
              className="rf-file-input"
              type="file"
              accept=".xlsx,.xls,.xlsm"
              onChange={handleFileChange}
              style={{ width: "100%" }}
            />
            <details style={{ fontSize: 12, color: "var(--muted)", marginTop: 8, lineHeight: 1.5 }}>
              <summary style={{ cursor: "pointer", minHeight: 44, paddingTop: 8 }}>Requisitos da planilha</summary>
              Colunas usadas: data da viagem, código do pedido, tipo serviço, horário, nome, chapa, celular, origem e destino.
              Trecho pela origem: Gavião Peixoto = volta para casa; outra origem = ida para {destinoLabel}.
              Vários horários de passageiro no mesmo pedido são agrupados; o sistema confere se bate com ao menos um deles.
              Pedidos com status <strong>Recusado pela central</strong> são ignorados.
            </details>
          </div>

          {parseInfo && (
            <div style={{
              background: "var(--s2)",
              borderRadius: 10,
              padding: "12px 14px",
              fontSize: 13,
              color: "var(--text)",
            }}>
              {fonteDados === "automatico" && parseInfo.start_date && parseInfo.end_date && (
                <>Período SGT: {formatDateBr(parseInfo.start_date)} a {formatDateBr(parseInfo.end_date)} · </>
              )}
              {parseInfo.total_linhas} linha(s) de passageiros · {parseInfo.total_programacoes} pedido(s) agrupados
              {parseInfo.programacoes_ignoradas > 0 && (
                <> · {parseInfo.programacoes_ignoradas} ignorada(s) com status Recusado pela central</>
              )}
            </div>
          )}

          {step === "upload" && (
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
              <Btn onClick={handlePreview} disabled={parsing || fetchingAuto || previewing || !parsedProgramacoes.length}>
                {previewing ? "Conferindo…" : "Conferir planilha"}
              </Btn>
              <span style={{ color: "var(--muted)", fontSize: 12 }}>ou</span>
              <Btn
                variant="secondary"
                onClick={handleFetchAuto}
                disabled={parsing || fetchingAuto || previewing}
              >
                {fetchingAuto ? "Buscando no SGT…" : "Buscar no SGT"}
              </Btn>
            </div>
          )}

          {(parsing || previewing || fetchingAuto) && (
            <div style={{ display: "flex", alignItems: "center", gap: 10, color: "var(--muted)", fontSize: 13 }}>
              <Spinner />
              {fetchingAuto
                ? "Buscando no portal Embraer… pode levar alguns minutos (login 2FA ou exportação)."
                : parsing
                  ? "Lendo planilha..."
                  : "Comparando com pedidos e cadastro..."}
            </div>
          )}
        </div>
      </Card>

      {step === "auditoria" && resumoAuditoria && (
        <>
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
            gap: 10,
            marginBottom: 16,
          }}>
            {[
              ["Pedidos", resumoAuditoria.programacoes],
              ["Com pendência", resumoAuditoria.com_pendencia],
              ["Não cadastradas", resumoAuditoria.nao_cadastradas],
              ["Divergências", resumoAuditoria.com_divergencia],
              ["Prontas p/ criar", resumoAuditoria.prontas_para_criar],
              ["Conferidas", resumoAuditoria.conferidas],
              ...(preview?.resumo?.ignoradas_recusado_central
                ? [["Ignoradas", preview.resumo.ignoradas_recusado_central]]
                : []),
            ].map(([label, value]) => (
              <Card key={label}>
                <div style={{ fontSize: 11, color: "var(--muted)", textTransform: "uppercase" }}>{label}</div>
                <div style={{ fontSize: 22, fontWeight: 700, marginTop: 4 }}>{value}</div>
              </Card>
            ))}
          </div>

          <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
            <Btn variant="ghost" onClick={() => setStep("upload")}>
              {fonteDados === "automatico" ? "Nova busca" : "Nova planilha"}
            </Btn>
            {resumoAuditoria.prontas_para_criar > 0 ? (
              <Btn variant="primary" onClick={() => setStep("acoes")}>
                Escolher pedidos para cadastrar ({resumoAuditoria.prontas_para_criar})
              </Btn>
            ) : (
              <Btn variant="ghost" disabled>
                Nenhum pedido pronto para cadastrar
              </Btn>
            )}
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {(auditoria?.grupos || []).map((grupo) => (
              <div key={grupo.id}>
                <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 10 }}>
                  {grupo.titulo} ({(grupo.itens || []).length})
                </h3>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {(grupo.itens || []).map((item) => (
                    <Card key={item.key}>
                      <div style={{ display: "flex", gap: 10, alignItems: "flex-start", justifyContent: "space-between" }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                            <SeveridadeBadge severidade={item.severidade} />
                            <span style={{ fontSize: 15, fontWeight: 700 }}>{item.id_pedido}</span>
                          </div>
                          <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 4 }}>
                            {item.contexto}
                            {item.programacao_id ? ` · Pedido #${item.programacao_id}` : ""}
                          </div>
                          <ul style={{
                            margin: "10px 0 0",
                            paddingLeft: 18,
                            display: "grid",
                            gap: 6,
                            fontSize: 13,
                            lineHeight: 1.45,
                          }}>
                            {(item.pendencias || []).map((pendencia) => (
                              <li key={`${item.key}-${pendencia.tipo}-${pendencia.texto}`}>
                                <span style={{
                                  color: pendencia.severidade === "erro"
                                    ? "#fca5a5"
                                    : pendencia.severidade === "aviso"
                                      ? "#fde047"
                                      : "#86efac",
                                }}>
                                  {pendencia.texto}
                                </span>
                                {pendencia.detalhe && (
                                  <span style={{ color: "var(--muted)", fontSize: 12 }}> — {pendencia.detalhe}</span>
                                )}
                              </li>
                            ))}
                          </ul>
                        </div>
                        {item.programacao_id && openPedidos && (
                          <Btn variant="ghost" onClick={openPedidos}>
                            Abrir pedidos
                          </Btn>
                        )}
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {resumoAuditoria.tem_pendencias && (
            <div style={{
              marginTop: 16,
              padding: "12px 14px",
              borderRadius: 10,
              background: "rgba(234,179,8,0.1)",
              fontSize: 13,
              color: "#fde047",
              lineHeight: 1.5,
            }}>
              Existem pendências na auditoria. Corrija em Pedidos ou no cadastro de funcionários antes de importar.
              Pedidos já cadastrados com divergências precisam ser ajustados manualmente.
            </div>
          )}
        </>
      )}

      {step === "acoes" && (
        <>
          <div style={{ marginBottom: 16 }}>
            <h3 style={{ fontSize: 16, fontWeight: 700 }}>Cadastrar pedidos</h3>
            <p style={{ fontSize: 13, color: "var(--muted)", marginTop: 4, lineHeight: 1.5 }}>
              Selecione os pedidos para cadastrar. Depois, você poderá organizar os passageiros nas vans em Pedidos → A fazer.
            </p>
          </div>

          <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
            <Btn variant="ghost" onClick={() => setStep("auditoria")}>
              Voltar à conferência
            </Btn>
            <Btn
              variant="primary"
              onClick={handleConfirm}
              disabled={confirming || !selectedKeys.size}
            >
              {confirming ? "Criando..." : `Cadastrar ${selectedKeys.size} pedido(s)`}
            </Btn>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {prontasParaCriar.length === 0 && (
              <div style={{ fontSize: 13, color: "var(--muted)" }}>
                Nenhum pedido disponível para criação.
              </div>
            )}
            {prontasParaCriar.map((item) => (
              <Card key={item.key}>
                <label style={{ display: "flex", gap: 12, alignItems: "flex-start", cursor: "pointer" }}>
                  <input
                    type="checkbox"
                    checked={selectedKeys.has(item.key)}
                    onChange={() => toggleSelection(item.key)}
                    style={{ marginTop: 3 }}
                  />
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 15 }}>{item.id_pedido}</div>
                    <div style={{ fontSize: 13, color: "var(--muted)", marginTop: 4 }}>
                      {formatDateBr(item.data)} · {formatEmbraerHorarios(item)} · {formatTrechoCurto(item.trecho, config)}
                    </div>
                    <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 4 }}>
                      {item.total_passageiros_embraer} passageiro(s)
                    </div>
                  </div>
                </label>
              </Card>
            ))}
          </div>
        </>
      )}

      {step === "upload" && !preview && !parsing && !fetchingAuto && parsedProgramacoes.length > 0 && (
        <div style={{ marginTop: 16, fontSize: 13, color: "var(--muted)" }}>
          {fonteDados === "automatico" ? "Busca concluída." : "Planilha lida."} Clique em <strong>Conferir planilha</strong> para ver o que está faltando ou divergente.
        </div>
      )}

      {toast && (
        <div style={{
          position: "fixed",
          bottom: 24,
          right: 24,
          zIndex: 200,
          background: toastType === "error" ? "#ef4444" : "#22c55e",
          color: "#fff",
          padding: "12px 20px",
          borderRadius: 10,
          fontWeight: 600,
          fontSize: 14,
          boxShadow: "0 4px 16px rgba(0,0,0,0.3)",
        }}>
          {toast}
        </div>
      )}
    </div>
  );
}
